/**
 * Cover layout geometry — handoff §3b (options + suggestion), §4 (geometry, framing).
 * Shared by the event page, the review-step previews and the layout picker thumbnails, so
 * the three can never disagree about where a photo goes.
 *
 * Pure. A "photo" is `{ id, src, r, fx, fy }`: `r` = width / height, `fx` / `fy` = the focal
 * point in % (CSS object-position). Every geometry value is a % of the cover box.
 */

export const LAYOUTS = ['auto', 'side', 'backdrop', 'split', 'poster', 'stack', 'row', 'mosaic'];
export const SPLITS = [60, 50, 40];
export const DEFAULT_RATIO = 1.5;
export const POSTER_ID = 'poster';

/** Portrait = taller than it is wide (with a little slack for near-squares). */
export const isPortrait = (p) => (p?.r ?? DEFAULT_RATIO) < 0.95;

export const LAYOUT_META = {
  side: { name: 'POSTER RIGHT', hint: 'Text on the left. The photo stands whole on the right.' },
  backdrop: { name: 'FULL BLEED', hint: 'The photo fills the cover. Text sits on a white fade.' },
  split: { name: 'SPLIT', hint: 'Photo 1 under the text, photo 2 beside it.' },
  poster: { name: 'LANDSCAPE + PORTRAIT', hint: 'Landscape under the text, portrait on the right.' },
  stack: { name: 'STACKED', hint: 'Photos on the right, one above the other.' },
  row: { name: 'ROW', hint: 'Photos on the right, side by side.' },
  mosaic: { name: 'MOSAIC', hint: 'Photo 1 leads, the rest in a grid.' },
};

/** Which layouts a cover with `n` photos may use (§3b table). */
export function validLayouts(n) {
  if (n <= 0) return [];
  if (n === 1) return ['side', 'backdrop'];
  if (n === 2) return ['split', 'poster', 'stack', 'row'];
  if (n === 3) return ['stack', 'row', 'mosaic'];
  return ['mosaic'];
}

/** The suggested layout for these photos (§3b "Suggested" column). */
export function autoLayout(photos = []) {
  const n = photos.length;
  const portraits = photos.filter(isPortrait).length;
  if (n === 0) return 'backdrop';
  if (n === 1) return isPortrait(photos[0]) ? 'side' : 'backdrop';
  if (n === 2) return portraits === 1 ? 'poster' : portraits === 2 ? 'row' : 'split';
  if (n === 3) return portraits >= 2 ? 'row' : 'stack';
  return 'mosaic';
}

/** The layout to draw: the saved one when it fits the photo count, else the suggestion. */
export function resolveLayout(saved, photos = []) {
  const valid = validLayouts(photos.length);
  return saved && saved !== 'auto' && valid.includes(saved) ? saved : autoLayout(photos);
}

export function normalizeSplit(split) {
  const s = parseInt(split, 10);
  return SPLITS.includes(s) ? s : 60;
}

/**
 * Tiles + the text column and fade widths for a layout (§4). `W` / `H` are the cover's pixel
 * size (the `side` and `poster` widths follow the photo's shape, so they need real numbers).
 * Each tile: `{ i, x, y, w, h, more }` — `i` indexes `photos`, `more` is the `+N` overlay count.
 */
export function coverGeom(layout, photos = [], W = 1216, H = 520, mobile = false, split = 60) {
  const n = photos.length;
  const t = (i, x, y, w, h, more = 0) => ({ i, x, y, w, h, more });
  if (!n) return { tiles: [], textW: 100, fadeW: 0 };
  const r = (i) => photos[i]?.r || DEFAULT_RATIO;
  if (layout === 'side') {
    const pw = mobile ? 100 : Math.min((H * r(0)) / W, 0.55) * 100;
    return { tiles: [t(0, 100 - pw, 0, pw, 100)], textW: 100 - pw, fadeW: 0 };
  }
  if (layout === 'backdrop') return { tiles: [t(0, 0, 0, 100, 100)], textW: 60, fadeW: 78 };
  if (layout === 'split' && n >= 2) {
    const s = normalizeSplit(split);
    return { tiles: [t(0, 0, 0, s, 100), t(1, s, 0, 100 - s, 100)], textW: s, fadeW: s };
  }
  if (layout === 'poster' && n >= 2) {
    const li = r(0) >= r(1) ? 0 : 1;
    const pi = 1 - li;
    const pw = Math.min((H * r(pi)) / W, mobile ? 0.5 : 0.45) * 100;
    return { tiles: [t(li, 0, 0, 100 - pw, 100), t(pi, 100 - pw, 0, pw, 100)], textW: 100 - pw, fadeW: 100 - pw };
  }
  if (layout === 'stack') {
    const pr = mobile ? 100 : 44;
    const k = Math.min(n, 3);
    return { tiles: photos.slice(0, k).map((_, i) => t(i, 100 - pr, (100 / k) * i, pr, 100 / k)), textW: 100 - pr, fadeW: 0 };
  }
  if (layout === 'row') {
    const k = Math.min(n, mobile ? 3 : 4);
    const pr = mobile ? 100 : k === 2 ? 50 : 60;
    return { tiles: photos.slice(0, k).map((_, i) => t(i, 100 - pr + (pr / k) * i, 0, pr / k, 100)), textW: 100 - pr, fadeW: 0 };
  }
  if (n === 1) return coverGeom(isPortrait(photos[0]) ? 'side' : 'backdrop', photos, W, H, mobile, split);
  // mosaic — photo 1 leads at 62%, up to four more in the right 38%; past five the last cell is +N.
  const extras = photos.slice(1);
  const shown = extras.length > 4 ? extras.slice(0, 3) : extras.slice(0, 4);
  const more = extras.length > 4 ? extras.length - 3 : 0;
  const cells = shown.map((_, k) => k + 1);
  if (more) cells.push(-1);
  const tiles = [t(0, 0, 0, 62, 100)];
  const R = 38;
  const c = cells.length;
  const place = (idx, x, y, w, h) => tiles.push(idx === -1 ? t(4, x, y, w, h, more) : t(idx, x, y, w, h));
  if (c === 1) place(cells[0], 62, 0, R, 100);
  else if (c === 2) { place(cells[0], 62, 0, R, 50); place(cells[1], 62, 50, R, 50); }
  else if (c === 3) { place(cells[0], 62, 0, R, 50); place(cells[1], 62, 50, R / 2, 50); place(cells[2], 62 + R / 2, 50, R / 2, 50); }
  else cells.forEach((idx, k) => place(idx, 62 + (k % 2) * (R / 2), Math.floor(k / 2) * 50, R / 2, 50));
  return { tiles, textW: 62, fadeW: 62 };
}

/** Cover height: 520px when the cover is at least 1100 wide, else 480; phones 300 (380 stacked). */
export function coverHeight(coverW, layout, mobile = false) {
  if (mobile) return layout === 'stack' ? 380 : 300;
  return coverW >= 1100 ? 520 : 480;
}

/** Title size by the pixel width the text column actually has (§2 Cover). */
export function titleSize(textPx) {
  if (textPx > 640) return 46;
  if (textPx > 480) return 38;
  return 30;
}

/** The white gutter a tile not at x=0 / y=0 carries on its left / top (4px, 3px on phones). */
export function tilePadding(tile, gap) {
  return `${tile.y > 0.5 ? gap : 0}px 0 0 ${tile.x > 0.5 ? gap : 0}px`;
}

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/**
 * Drag-to-reframe (§4 Framing): only the cropped axis moves. `ox` / `oy` = how much of the
 * photo overflows the box along that axis at object-fit: cover.
 */
export function dragFocal({ r = DEFAULT_RATIO, boxW, boxH, startFx = 50, startFy = 50, dx = 0, dy = 0 }) {
  if (!boxW || !boxH) return { fx: startFx, fy: startFy };
  const wider = r > boxW / boxH;
  const ox = wider ? boxH * r - boxW : 0;
  const oy = wider ? 0 : boxW / r - boxH;
  return {
    fx: ox > 1 ? Math.round(clamp(startFx - (dx / ox) * 100, 0, 100)) : startFx,
    fy: oy > 1 ? Math.round(clamp(startFy - (dy / oy) * 100, 0, 100)) : startFy,
  };
}

/** One-line note under the picker per layout (§3b). */
export function layoutNote(layout, split = 60, count = 0) {
  const s = normalizeSplit(split);
  switch (layout) {
    case 'side': return 'One photo. It keeps its own shape — nothing gets cropped off a flyer.';
    case 'backdrop': return 'Drag the photo so the busy part sits on the right, clear of the title.';
    case 'split': return `Photo 1 takes ${s}% under the text. Photo 2 takes the other ${100 - s}%.`;
    case 'poster': return 'Set automatically for a landscape and a portrait. Order doesn’t matter here.';
    case 'stack': return 'Photo 1 on top. Reverse to flip them.';
    case 'row': return 'Photo 1 sits next to the text. Reverse to run them the other way.';
    case 'mosaic': return count > 5 ? `Photo 1 leads. Past five, the last tile shows +${count - 4}.` : 'Photo 1 leads. Up to four more fill the grid.';
    default: return '';
  }
}

/**
 * The event's photos in cover order: the poster (id "poster") and every gallery image, sorted by
 * `posterSortOrder` / `sortOrder` (poster first on a tie). Videos and embeds never make the cover.
 * `ratios` — measured aspect ratios by src, overlaid when the DTO has no width/height.
 */
export function allCoverPhotos(event, ratios = {}) {
  if (!event) return [];
  const list = [];
  if (event.posterUrl) {
    list.push({ id: POSTER_ID, src: event.posterUrl, r: ratios[event.posterUrl] || null, fx: num(event.posterFocalX), fy: num(event.posterFocalY), sort: Number(event.posterSortOrder || 0), tie: 0 });
  }
  (Array.isArray(event.media) ? event.media : []).forEach((m, i) => {
    if (!m || (m.kind && m.kind !== 'image')) return;
    const src = m.url || m.thumbnailUrl;
    if (!src) return;
    const own = m.width && m.height ? m.width / m.height : null;
    list.push({ id: m.id || `m${i}`, src, r: ratios[src] || own || null, fx: num(m.focalX), fy: num(m.focalY), sort: Number(m.sortOrder ?? i), tie: 1 });
  });
  list.sort((a, b) => (a.sort - b.sort) || (a.tie - b.tie));
  return list.map(({ sort, tie, ...p }) => ({ ...p, r: p.r || DEFAULT_RATIO, measured: !!p.r }));
}

/** The photos that go in the cover: the first `coverPhotoCount` (all when unset). */
export function coverPhotos(event, ratios = {}) {
  const all = allCoverPhotos(event, ratios);
  const count = Number(event?.coverPhotoCount || 0);
  return count > 0 ? all.slice(0, count) : all;
}

/** The grid-card photo: the one the host picked, else the poster, else the first photo. */
export function cardPhoto(event, photos = null) {
  const all = photos || allCoverPhotos(event);
  if (!all.length) return null;
  if (event?.cardMediaId) {
    const hit = all.find((p) => p.id === event.cardMediaId);
    if (hit) return hit;
  }
  return all.find((p) => p.id === POSTER_ID) || all[0];
}

function num(v, fallback = 50) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : fallback;
}

export const focalPosition = (p) => `${num(p?.fx)}% ${num(p?.fy)}%`;
